import { inject, Injectable } from '@angular/core';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Basket } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { Customer } from '@df/session/api/customer';
import { createGiftboxBasketItem, GiftboxBasketItem, IGiftboxBasketItemData } from './giftbox-basket-item';
/**
 * Extend basket data
 * to include optionsl gifting data
 */
declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Basket {
        interface BasketData<
            TItemId extends number | string = number,
            TProductId extends number | string = number,
            TQuoteId extends number | string = number
        > {
            gifting?: IGiftingBasketData;
        }
    }
}

/**
 * Gifting data passed with customer basket data
 * Contins information about number of boxes in basket
 * and items reference
 */
export interface IGiftingBasketData {
    // number of boxes in basket
    count: number;
    // total boxes price
    total: number;
    // basket items data
    items: IGiftboxBasketItemData[];
}

@Injectable({ providedIn: 'root' })
export class GiftboxService {
    /**
     * Gift box product
     * @type {Product}
     */
    protected giftingProduct?: Product;

    /**
     * Customer basket service
     * @type {Basket}
     */
    public basket: Basket;

    /**
     * Basket items
     * (not basket items data)
     *
     * Buffer is reset when basket updates
     *
     */
    protected itemsBuffer?: GiftboxBasketItem[];

    /**
     * Mesage option budder
     */
    protected _messageOption;

    /**
     * Mesage option budder
     */
    protected _nameOption;

    protected _emptyBox?: GiftboxBasketItem;

    readonly app = inject(App);
    readonly dfEventService = inject(DfEventService);

    /**
     * Class constructor and injections
     * @type {Array}
     */
    public constructor(public customer: Customer) {
        this.basket = customer.basket;

        this.dfEventService.success(event => {
            if (event.id === 'basketDataChange') {
                this.itemsBuffer = undefined;
            }
        });
    }

    /////////////////////////////////////////////////////////////////
    ///
    /// BOX PRODUCT MODEL
    ///
    /////////////////////////////////////////////////////////////////

    /**
     * Loads gifting product (finds it by isGiftBox in its data)
     */
    public async loadGiftingProduct(): Promise<Product[]> {
        const result = await this.app.resources.product.find({
            verbosity: 3,
            limit: 1,
            filter: {
                isGiftBox: true
            }
        });

        const productId = result && result[0] && result[0].id;

        if (productId) {
            this.giftingProduct = <Product>this.app.resources.product.get(productId);
        }

        return result;
    }

    /**
     * Takes gifting product
     * NEEDS TO BE LOADED FIRST, see loadGiftingProduct
     */
    public getGiftingProduct(): Product | undefined {
        return this.giftingProduct;
    }

    /////////////////////////////////////////////////////////////////
    ///
    /// BOXES IN BASKET
    ///
    /////////////////////////////////////////////////////////////////

    /**
     * Retrieves number of gift boxes in basket
     */
    public get boxesQty(): number {
        const giftingData = this.basket.data?.['gifting'];

        return giftingData ? giftingData.count : 0;
    }

    /**
     * Retrieves number of gift boxes in basket
     */
    public get total(): number {
        const giftingData = this.basket.data?.['gifting'];

        return giftingData ? giftingData.total : 0;
    }

    /**
     * Box items data
     * @return {BasketItem[]}
     */
    public get itemsData(): IGiftboxBasketItemData[] {
        const giftingData = this.basket.data?.['gifting'];

        return giftingData ? giftingData.items : [];
    }

    public get itemsCount(): number {
        return this.itemsBuffer?.length || 0;
    }

    /**
     * Box items
     * @return {BasketItem[]}
     */
    public async getBoxItems(): Promise<GiftboxBasketItem[]> {
        if (!this.giftingProduct) {
            await this.loadGiftingProduct();
        }

        return this._getBoxItems();
    }

    _getBoxItems(): GiftboxBasketItem[] {
        const giftingData = this.basket.data?.['gifting'];

        if (!giftingData || !this.giftingProduct) {
            return [];
        }

        if (this.itemsBuffer === undefined) {
            let index = 1;

            const itemsBuffer = giftingData.items.map(data => {
                return this.createGiftboxBasketItem(index++, data);
            });

            this.itemsBuffer = itemsBuffer;
        }

        return this.itemsBuffer;
    }

    /**
     *  creates new giftbox basket item
     */
    createGiftboxBasketItem(index, data): GiftboxBasketItem {
        return createGiftboxBasketItem(this, this.customer, index++, data, this.basket);
    }

    /**
     * Retrieves those basket items that can be boxed
     * @return {array}
     */
    public getGiftingAllowedBasketItems(): BasketItem[] {
        return this.basket.items.filter(item => {
            const product = item.product;

            return product && product.data && !product.data['noGifting'];
        });
    }

    get isGiftingAvailableForBasket(): boolean {
        return this.getGiftingAllowedBasketItems().length > 0;
    }

    /////////////////////////////////////////////////////////////////
    ///
    /// CUSTOM OPTIONS GETTERS
    ///
    /////////////////////////////////////////////////////////////////

    /**
     * Custom option getter for option to contain gift message copy
     */
    public getMessageOption() {
        if (this._messageOption === undefined) {
            const product = this.getGiftingProduct();

            if (product) {
                this._messageOption = product.getCustomOption(/message/i) || null;
            }
        }

        return this._messageOption || undefined;
    }

    /**
     * Custom option getter for option to contain name copy
     */
    public getNameOption() {
        if (this._nameOption === undefined) {
            const product = this.getGiftingProduct();
            if (product) {
                this._nameOption = product.getCustomOption(/name/i) || null;
            }
        }
        return this._nameOption || undefined;
    }
    /**
     * Custom option getter for option where we contain
     * information about product to put in that box
     */
    public getContentsOption() {
        const product = this.getGiftingProduct();
        if (product) {
            return product.getCustomOption(/contents/i);
        }
        return undefined;
    }
}
